import { Flex, Upload, message } from 'antd';
import { Delete, Import, Pin, Plus } from '../../icons';
import { Button, DefaultIconButton, Text, Tooltip } from '../../Ant';
import {
  StyledUpload,
  UploadedFile,
  UploadWrapper,
} from '../../../assets/styles/components/fileUpload.styled';
import { useTranslation } from 'react-i18next';
import { FileInfo } from '../../../interface/notes';
import { downloadBase64File } from '../../../constants/utilityFunctions';
import { MAX_FILE_SIZE } from '../../../constants/constants';

const FileUpload = ({
  onFile,
  fileInfo,
}: {
  onFile: (file: FileInfo | null) => void;
  fileInfo: FileInfo | null;
}) => {
  const { t } = useTranslation();

  const beforeUpload = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      message.error('File must be smaller than 1MB!');
      return Upload.LIST_IGNORE;
    }
    const fileInfo = {
      name: file.name,
      size: file.size,
      file,
    };
    onFile(fileInfo);
    return false;
  };

  const handleRemove = () => {
    onFile(null);
  };

  const handlePreview = () => {
    if (!fileInfo) return;
    if (fileInfo.file instanceof File) {
      const reader = new FileReader();
      reader.onload = function (e) {
        const base64 = (e.target?.result as string)?.split(',')[1];
        if (base64) {
          downloadBase64File(base64, fileInfo.name, fileInfo.file.type);
        }
      };
      reader.readAsDataURL(fileInfo.file);
    } else if (typeof fileInfo.file === 'string') {
      downloadBase64File(fileInfo.file, fileInfo.name, '');
    }
  };

  return (
    <UploadWrapper>
      <Flex gap={15} className="w-100" justify="space-between" align="center">
        <Flex vertical>
          <Text
            data-testid="add-attachment-title"
            $fontsize="13px"
            $weight={500}
            $color="title"
            $lineheight="20px"
          >
            {t('note:addAttachment')}
          </Text>
          <Text
            data-testid="add-attachment-info"
            $fontsize="11px"
            $weight={400}
            $color="description"
            $lineheight="17px"
          >
            {t('note:addAttachmentInfo')}
          </Text>
        </Flex>
        <StyledUpload
          beforeUpload={beforeUpload}
          showUploadList={false}
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
          disabled={!!fileInfo}
        >
          <Button
            aria-label={t('note:addAttachment')}
            data-testid="add-attachment-button"
            disabled={!!fileInfo}
            block
            maxwidth="30px"
            icon={<Plus className="fs-18" />}
            type="primary"
          />
        </StyledUpload>
      </Flex>
      {fileInfo && (
        <UploadedFile>
          <Flex align="center" gap={5}>
            <Pin className="attached-file-icon" />
            <Flex vertical>
              <Text
                $fontsize="12px"
                $lineheight="20px"
                $color="title"
                className="attached-file-name"
                data-testid="attached-file-name"
              >
                {fileInfo.name}
              </Text>
              <Text
                data-testid="attached-file-size"
                $fontsize="11px"
                $lineheight="20px"
                $color="description"
              >
                {(fileInfo.size / 1024).toFixed(0)} KB
              </Text>
            </Flex>
          </Flex>
          <Flex gap={5}>
            <Tooltip title={t('note:delete')} placement="top" arrow={false}>
              <DefaultIconButton
                aria-label={t('note:delete')}
                type="text"
                data-testid="remove-attachment-button"
                className="attachment-cta"
                icon={<Delete />}
                onClick={handleRemove}
              />
            </Tooltip>
            <Tooltip title={t('note:download')} placement="top" arrow={false}>
              <DefaultIconButton
                aria-label={t('note:download')}
                type="text"
                data-testid="view-attachment-button"
                className="attachment-cta"
                icon={<Import />}
                onClick={handlePreview}
              />
            </Tooltip>
          </Flex>
        </UploadedFile>
      )}
    </UploadWrapper>
  );
};

export default FileUpload;
